import {
  applyDialCode,
  createIdentifierSwapHandlers,
  detectIdentifierMode,
  IdentifierMode,
} from '@lib/utils/identifier'
import React, { useEffect, useRef, useState } from 'react'

type CountryData = { dialCode?: string }

/**
 * Shared state for a field that accepts either a phone number or an email.
 *
 * Three visual states, derived from `mode` plus the value rather than a third
 * enum, so nothing downstream has to learn a new type:
 *
 *   empty + phone mode  ->  plain input, placeholder, no flag  (resting state)
 *   phone mode + value  ->  the react-phone-input-2 widget
 *   email mode          ->  plain input
 *
 * Resting plain is deliberate. A flag and a prefilled `+91` read as
 * "phone only" no matter what the label says, which is the confusion this
 * field exists to avoid - and it is how Airbnb and Uber behave. See the
 * research section on CE-5740 for the comparison.
 */
export const useIdentifierSwap = (
  mode: IdentifierMode,
  value: string,
  onChange: (value: string, mode: IdentifierMode) => void,
  onEnter?: () => void
) => {
  const plainInputRef = useRef<HTMLInputElement>(null)
  const hasSwappedRef = useRef(false)
  // Set when something was typed into the plain input before the phone widget
  // existed. The dial code is only knowable once the widget mounts and reports
  // it, so the prefixing waits for its focus event.
  const [awaitingDialCode, setAwaitingDialCode] = useState(false)
  // The widget only reveals the dial code through its callbacks, and we need
  // it later to tell the user's digits apart from the prefix it added.
  const dialCodeRef = useRef('')
  const showPhoneWidget = mode === 'phone' && Boolean(value)

  const swapHandlers = createIdentifierSwapHandlers(
    (nextValue, nextMode) => {
      if (nextMode === 'email') hasSwappedRef.current = true
      onChange(nextValue, nextMode)
    },
    onEnter,
    // Strip the prefix the widget added, so a swap to email keeps the digits
    // the user typed rather than starting over.
    () => {
      const dialCode = dialCodeRef.current
      return dialCode && value.startsWith(dialCode)
        ? value.slice(dialCode.length)
        : value
    }
  )

  const handlePlainChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value
    if (!nextValue) {
      hasSwappedRef.current = false
      setAwaitingDialCode(false)
      onChange('', 'phone')
      return
    }

    const nextMode = detectIdentifierMode(nextValue)
    if (nextMode === 'phone') {
      // Hand the digits to the widget rather than keeping them here: it owns
      // the dial code and the mask. `onPhoneWidgetFocus` completes the handoff.
      setAwaitingDialCode(true)
      // Put back the dial code the swap to email stripped. The widget guesses
      // a country from any value longer than one digit rather than taking the
      // one we hand it, so deleting the "@" out of "123@" would remount it as
      // the US - a country nobody chose - instead of the one the IP resolved.
      onChange(applyDialCode(nextValue, dialCodeRef.current), 'phone')
      return
    }
    onChange(nextValue, nextMode)
  }

  /**
   * Prefix the digits typed before the widget existed with its dial code.
   *
   * `react-phone-input-2` only reveals the resolved country through its
   * callbacks, so this is the first moment the dial code is knowable. Mounting
   * with `autoFocus` guarantees it fires.
   */
  const onPhoneWidgetFocus = (
    _event: React.FocusEvent<HTMLInputElement>,
    data: CountryData | Record<string, never>
  ) => {
    const dialCode = (data as CountryData)?.dialCode
    // Remembered before the early returns below: the swap back to email needs
    // it even when there is nothing to hand over.
    if (dialCode) dialCodeRef.current = dialCode
    if (!awaitingDialCode) return
    setAwaitingDialCode(false)
    if (!dialCode) return
    const withDialCode = applyDialCode(value, dialCode)
    if (withDialCode === value) return
    onChange(withDialCode, 'phone')
  }

  // Whether the widget was mounted on the previous render, so focus is only
  // restored when it actually goes away - not on first mount, where stealing
  // focus would be wrong.
  const wasShowingWidgetRef = useRef(false)

  useEffect(() => {
    const widgetJustClosed = wasShowingWidgetRef.current && !showPhoneWidget
    wasShowingWidgetRef.current = showPhoneWidget
    // Either the user typed an email character, or they cleared the number and
    // the widget unmounted. React swaps in a different element for both, so
    // without this the caret is lost mid-typing.
    if (showPhoneWidget || !(hasSwappedRef.current || widgetJustClosed)) return
    const input = plainInputRef.current
    if (!input) return
    input.focus()
    input.setSelectionRange(input.value.length, input.value.length)
  }, [showPhoneWidget])

  return {
    showPhoneWidget,
    /**
     * What the field has decided it is, for labelling.
     *
     * Follows the widget, so the caption and the flag always agree: neutral
     * while the field is empty, then phone or email once something is typed.
     */
    identifierState: (mode === 'email'
      ? 'email'
      : showPhoneWidget
      ? 'phone'
      : 'neutral') as 'neutral' | 'phone' | 'email',
    plainInputRef,
    swapHandlers,
    handlePlainChange,
    onPhoneWidgetFocus,
    // Kept under the old name so callers reading `emailInputRef` still work.
    emailInputRef: plainInputRef,
    handleEmailChange: handlePlainChange,
  }
}
